/*
  A module providing event listeners and clean up functions to open and close dropdowns.

  Requires classes for dropdown, dropdown-trigger and dropdown-menu. Usage:

  ```html
  <div class="dropdown group" v-dropdown>
    <button
      class="dropdown-trigger"
      aria-expanded="false"
      aria-controls="my-dropdown"
    >Toggle dropdown</button>
    <div class="dropdown-menu hidden group-[.open]:block" id="my-dropdown">
      <!-- Dropdown content -->
    </div>
  </div>
  ```
*/
import type { Directive } from 'vue';

declare global {
  interface HTMLElement {
    _clickOutsideEvent?: (event: MouseEvent) => void;
  }
}

export function closeDropdown(el: HTMLElement) {
  el.querySelector<HTMLElement>('.dropdown-trigger')!.ariaExpanded = 'false';
  el.classList.remove('open');
}

function toggleDropdown(event: MouseEvent) {
  const trigger = event.target;
  if (!(trigger instanceof HTMLElement) && !(trigger instanceof SVGElement)) return;

  trigger.ariaExpanded = trigger.ariaExpanded === 'true' ? 'false' : 'true';
  trigger.closest<HTMLElement>('.dropdown')!.classList.toggle('open');
}

const dropdown: Directive<HTMLElement, (event: MouseEvent, el: HTMLElement) => void> = {
  // Close dropdown when clicking outside
  beforeMount(el) {
    el._clickOutsideEvent = function (event) {
      const { target } = event;
      if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) return;

      if (el !== event.target && !el.contains(target)) {
        closeDropdown(el);
      }
    };
    document.body.addEventListener('click', el._clickOutsideEvent);
  },
  // Toggle dropdown when clicking on trigger
  mounted(el) {
    el.querySelector<HTMLElement>('.dropdown-trigger')!.addEventListener('click', toggleDropdown);
  },
  unmounted(el) {
    // Clean up click
    el.querySelector<HTMLElement>('.dropdown-trigger')!.removeEventListener(
      'click',
      toggleDropdown,
    );

    // Clean up click outside
    document.body.removeEventListener('click', el._clickOutsideEvent!);
    el.removeEventListener('click', stop);
    delete el._clickOutsideEvent;
  },
};

export default dropdown;
